import { Link, NavLink, useLocation, useMatch } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type DragEvent, type ReactNode } from 'react';
import {
  Inbox, AtSign, Users, User as UserIcon, Plus, Settings, Activity, Download, LayoutGrid,
  PanelLeftClose, PanelLeftOpen, Rocket, Chrome, Plug, Sparkles, Check, ChevronDown, ChevronLeft,
  MessageSquare, Building2, Gauge, XCircle, BarChart3, SlidersHorizontal,
  Briefcase,
  Clapperboard,
  MonitorPlay,
  LayoutDashboard,
} from 'lucide-react';
import { EXTENSION_INSTALL_PATH, type LiveEvent } from '@vd/types';
import { RollingCount, Tooltip, cn, useToast } from '@vd/ui';
import { useAuth, useCurrentCompany } from '../auth/AuthProvider';
import { api } from '../../lib/api';
import { useGuardedNavigate } from '../../app/NavigationGuard';
import { CreateProjectModal } from '../modals/CreateProjectModal';
import { orderByMru, refillSidebarIds } from './projectMru';
import {
  TOADO_CWS_URL,
  downloadSkill,
  readSkillDownloaded,
  SKILL_DOWNLOADED_EVENT,
} from '../onboarding/getStarted';
import styles from './Sidebar.module.css';
import { widgetSetupProjectId } from '../../lib/widgetSetupProject';
import { EVERY_SCREEN } from '../../lib/screenNames';
import { ProjectMark } from '../projects/ProjectMark';
import { openTickets, projectCountsLine } from '../projects/projectCounts';

const SIDEBAR_PROJECT_LIMIT = 6;
const SIDEBAR_ORDER_KEY = (companyId: string) => `vd-sidebar-projects:${companyId}`;

/**
 * Module-level stable reference for "no projects yet." Used as the
 * useQuery default below so the destructure doesn't mint a fresh `[]`
 * on every render — that fresh array's identity churn would otherwise
 * trip our seeding useEffect's dep array, calling setState every
 * render and looping until React's max-update-depth guard fires.
 *
 * Type-asserted to satisfy ProjectListItem[] without pulling the type
 * import — it's only ever empty so the contained shape is moot.
 */
const EMPTY_PROJECTS: never[] = [];

function loadOrder(companyId: string): string[] {
  try {
    const raw = localStorage.getItem(SIDEBAR_ORDER_KEY(companyId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}
function saveOrder(companyId: string, ids: string[]) {
  try { localStorage.setItem(SIDEBAR_ORDER_KEY(companyId), JSON.stringify(ids)); } catch { /* quota/disabled */ }
}

// Per-section expand/collapse, persisted so a user's choice sticks across
// reloads. Returns null when the user has never toggled a section, so the
// caller can apply its own default (e.g. Get started collapses once done).
const NAV_SECTION_KEY = (key: string) => `vd-nav-section:${key}`;
function readNavSectionOpen(key: string): boolean | null {
  try {
    const raw = localStorage.getItem(NAV_SECTION_KEY(key));
    return raw === null ? null : raw === '1';
  } catch { return null; }
}
function writeNavSectionOpen(key: string, open: boolean): void {
  try { localStorage.setItem(NAV_SECTION_KEY(key), open ? '1' : '0'); } catch { /* quota/disabled */ }
}

/**
 * A sidebar section with a collapsible body. The ENTIRE header row (label +
 * chevron) is one button, so a click anywhere on it toggles — not only the
 * arrow. Open/closed persists per `storageKey`; `defaultOpen` applies until the
 * user first toggles. In the icon-only rail there is no header, so the section
 * always shows its icons (matching the pre-existing collapsed behavior).
 */
export function CollapsibleNavSection({
  title, storageKey, defaultOpen = true, railCollapsed, headerRight, children,
}: {
  title: string;
  storageKey: string;
  defaultOpen?: boolean;
  railCollapsed: boolean;
  headerRight?: ReactNode;
  children: ReactNode;
}) {
  const [override, setOverride] = useState<boolean | null>(() => readNavSectionOpen(storageKey));
  const open = override ?? defaultOpen;
  const toggle = (): void => {
    const next = !open;
    setOverride(next);
    writeNavSectionOpen(storageKey, next);
  };

  if (railCollapsed) {
    return <div className={styles.section}>{children}</div>;
  }
  return (
    <div className={styles.section}>
      <div className={styles.label}>
        <button type="button" className={styles.sectionToggle} onClick={toggle} aria-expanded={open}>
          <span>{title}</span>
          {open ? <ChevronDown size={12} /> : <ChevronLeft size={12} />}
        </button>
        {headerRight}
      </div>
      {open && children}
    </div>
  );
}

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Opened over the page at a small width, where its track stays an icon strip (TOADO-825). */
  drawer?: boolean;
}

/** The live events that can change an inbox count. */
const INBOX_COUNT_EVENTS: ReadonlySet<LiveEvent['type']> = new Set<LiveEvent['type']>([
  'ticket.created', 'ticket.updated', 'ticket.assigned', 'ticket.moved',
  'ticket.archived', 'ticket.restored', 'ticket.deleted', 'comment.created',
]);

export function Sidebar({ collapsed, onToggle, drawer = false }: SidebarProps) {
  const { currentCompanyId, user, features } = useAuth();
  const isOwner = useCurrentCompany()?.role === 'owner';
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  // IMPORTANT: default to the module-level EMPTY_PROJECTS, NOT inline `[]`.
  // Inline `[]` mints a fresh array reference on every render while
  // `data` is undefined (initial load, refetch, or query disabled),
  // and that fresh ref churn cascades through the seeding useEffect's
  // dep array → setState → re-render → setState → "Maximum update
  // depth exceeded" until React's guard kicks in.
  const { data: projects = EMPTY_PROJECTS } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([])),
    enabled: !!currentCompanyId,
  });

  // The sidebar's ordered project slice. Separate from MRU — MRU used to
  // drive this and it reordered on every click, which turned out to feel
  // jumpy. Now the list is stable and user-controlled: pins only change
  // when (a) the user opens a project that isn't pinned (appended at the
  // end, oldest pin falls off the end) or (b) the user drag-drops to
  // reorder when the nav is expanded.
  const [sidebarIds, setSidebarIds] = useState<string[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Seed from MRU the first time we see a company (or after all pinned
  // projects were deleted — localStorage is back to empty). Respecting the
  // stored list otherwise means the user's drag-drop order wins on reload.
  useEffect(() => {
    if (!currentCompanyId) { setSidebarIds([]); return; }
    const stored = loadOrder(currentCompanyId);
    // Prune IDs for projects the user no longer has access to (deleted,
    // left the project, etc.) so stale entries don't waste a pin slot.
    const liveIds = new Set(projects.map((p) => p.id));
    const pruned = stored.filter((id) => liveIds.has(id));
    if (pruned.length > 0) {
      if (pruned.length !== stored.length) {
        // A project left the list: the most recently opened of the rest
        // fills the space at the bottom (Jason, 17 Oct).
        const filled = refillSidebarIds(pruned, projects, SIDEBAR_PROJECT_LIMIT);
        setSidebarIds(filled);
        saveOrder(currentCompanyId, filled);
        return;
      }
      setSidebarIds(pruned);
      return;
    }
    // Empty (or fully pruned) — seed from MRU so a brand-new user gets a
    // reasonable first view. After this seed runs, clicks won't reorder.
    const seed = orderByMru(projects).slice(0, SIDEBAR_PROJECT_LIMIT).map((p) => p.id);
    setSidebarIds(seed);
    if (seed.length > 0) saveOrder(currentCompanyId, seed);
  }, [currentCompanyId, projects]);

  // When the user navigates to a project page, ensure it's in the pin list.
  // If it's already there, no-op. If not, append at the end and evict the
  // old last item if the list is at capacity.
  const projectMatch = useMatch('/projects/:projectId/*');
  const currentProjectId = projectMatch?.params.projectId;
  const liveIdSet = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);

  useEffect(() => {
    if (!currentCompanyId || !currentProjectId) return;
    if (!liveIdSet.has(currentProjectId)) return; // cross-company or stale
    setSidebarIds((prev) => {
      if (prev.includes(currentProjectId)) return prev;
      const next = prev.length >= SIDEBAR_PROJECT_LIMIT
        ? [...prev.slice(0, SIDEBAR_PROJECT_LIMIT - 1), currentProjectId]
        : [...prev, currentProjectId];
      saveOrder(currentCompanyId, next);
      return next;
    });
  }, [currentCompanyId, currentProjectId, liveIdSet]);

  // Resolve ordered IDs to project objects; drop any that don't resolve
  // (project was deleted between stores and renders).
  const projectsById = useMemo(() => {
    const map = new Map<string, typeof projects[number]>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);
  const visibleProjects = useMemo(
    () => sidebarIds.map((id) => projectsById.get(id)).filter((p): p is typeof projects[number] => !!p),
    [sidebarIds, projectsById],
  );

  const reorderTo = useCallback((sourceId: string, targetId: string) => {
    if (sourceId === targetId || !currentCompanyId) return;
    setSidebarIds((prev) => {
      const srcIdx = prev.indexOf(sourceId);
      const tgtIdx = prev.indexOf(targetId);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      const next = [...prev];
      next.splice(srcIdx, 1);
      // Insert AT the target's current position after removal — natural
      // "drag onto another row" behavior for both directions.
      const insertAt = next.indexOf(targetId);
      next.splice(insertAt, 0, sourceId);
      saveOrder(currentCompanyId, next);
      return next;
    });
  }, [currentCompanyId]);

  const handleCreate = async (data: { name: string; color: string; urlPattern?: string }) => {
    if (!currentCompanyId) return;
    try {
      const project = await api.createProject(currentCompanyId, {
        name: data.name,
        color: data.color,
        urlPattern: data.urlPattern,
      });
      await qc.invalidateQueries({ queryKey: ['projects', currentCompanyId] });
      toast.push({ variant: 'success', title: 'Project created', description: project.name });
      navigate(`/projects/${project.id}/board`);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not create project', description: (e as Error).message });
    }
  };


  // The numbers beside the lists (TOADO-740): open tickets for Assigned, what is
  // new since last opened for Mentioned and Team. Created by me has none (TOADO-950).
  const { data: inboxCounts } = useQuery({
    queryKey: ['inbox-counts', currentCompanyId],
    queryFn: () => api.getInboxCounts(currentCompanyId!),
    enabled: !!currentCompanyId,
    refetchOnWindowFocus: true,
  });
  // Kept current without a reload: any ticket or comment event asks again,
  // once a burst of them has settled.
  useEffect(() => {
    if (!currentCompanyId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = api.subscribe((event) => {
      if (!INBOX_COUNT_EVENTS.has(event.type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => { void qc.invalidateQueries({ queryKey: ['inbox-counts', currentCompanyId] }); }, 500);
    });
    return () => { clearTimeout(timer); unsubscribe(); };
  }, [currentCompanyId, qc]);

  // Get started section state — completion flags + collapse override.
  // homeState's 3 booleans are server-derived; skillDownloaded is the
  // local-only flag tracked via the helper module's localStorage key.
  // For this workspace, as Home asks: the two share this cache key, so an
  // account-wide answer here overwrote Home's and could show a new
  // workspace the full Dashboard (TOADO-980).
  const { data: homeState } = useQuery({
    queryKey: ['home-state', currentCompanyId],
    queryFn: () => api.getHomeState(currentCompanyId),
    enabled: !!currentCompanyId,
    refetchOnWindowFocus: true,
  });
  const [skillDownloaded, setSkillDownloaded] = useState<boolean>(() => readSkillDownloaded());
  // Keep skillDownloaded in sync if the user triggers download from
  // a different surface OR a different tab:
  //   - `storage` event covers CROSS-tab writes (browser spec)
  //   - `vd:skill-downloaded` covers SAME-tab writes (storage doesn't
  //     fire for the tab that wrote it; the helper dispatches this
  //     event after every flag write so both surfaces stay in sync)
  useEffect(() => {
    const refresh = () => setSkillDownloaded(readSkillDownloaded());
    window.addEventListener(SKILL_DOWNLOADED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SKILL_DOWNLOADED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const gsHasProject = !!homeState?.hasProject;
  const gsExtensionInstalled = !!homeState?.extensionInstalled;
  const gsWidgetInstalled = !!homeState?.widgetInstalled;
  const gsMcpConfigured = !!homeState?.mcpConfigured;
  const gsSkillDownloaded = skillDownloaded;
  // The extension and the embedded widget are two ways to capture; either one
  // satisfies the "set up a capture path" part of onboarding (mirrors the
  // api's widgetInstalled note), so the checklist completes with one of them.
  const gsAllDone = gsHasProject && (gsExtensionInstalled || gsWidgetInstalled)
    && gsMcpConfigured && gsSkillDownloaded;
  // Widget setup is per-project: point at the most recently opened project's
  // Widget tab when it is in this workspace, else this workspace's first
  // project, else the projects list (so a user with no project yet still
  // lands somewhere to make one). See widgetSetupProjectId.
  const widgetProjectId = widgetSetupProjectId(homeState?.lastProjectId, projects);
  const widgetSetupTarget = widgetProjectId
    // `setup=1` opens the setup wizard there (TOADO-579).
    ? `/projects/${widgetProjectId}/settings?tab=widget&setup=1`
    : '/projects';

  return (
    // On every screen of every page, so pins on it are too (TOADO-701). The
    // extension counts the top bar's `header` as the site's own without being
    // told, but never an `aside`.
    <aside
      className={cn(styles.sidebar, collapsed && styles.collapsed, drawer && styles.drawer)}
      aria-label="Primary"
      data-toado-view={EVERY_SCREEN}
    >
      <div className={styles.section}>
        {collapsed ? (
          <Tooltip content="Open sidebar · [" placement="right">
            <button
              type="button"
              className={cn(styles.item, styles.toggleRail)}
              onClick={onToggle}
              aria-label="Open sidebar"
            >
              <span className={styles.itemIcon}><PanelLeftOpen size={14} /></span>
            </button>
          </Tooltip>
        ) : (
          <div className={styles.label}>
            {/* Dashboard, Video recordings and what was My tickets, in one group (Jason, 16 Oct).
                It keeps the close control: it is always open, so the control stays at the top. */}
            Overview
            <Tooltip content="Close sidebar · [" placement="bottom">
              <button
                type="button"
                className={styles.iconMini}
                onClick={onToggle}
                aria-label="Close sidebar"
              >
                <PanelLeftClose size={12} />
              </button>
            </Tooltip>
          </div>
        )}
        {/* Where the frog goes (TOADO-722). */}
        <SideItem to="/" icon={<LayoutDashboard size={14} />} label="Dashboard" collapsed={collapsed} />
        {features.v2Recorder && (
          <SideItem to="/recordings" icon={<Clapperboard size={14} />} label="Video recordings" collapsed={collapsed} />
        )}
        <SideItem to="/inbox/assigned" icon={<Inbox size={14} />} label="Assigned to me" collapsed={collapsed} count={inboxCounts?.assigned} />
        {/* A number here means something is waiting for you (TOADO-950): Assigned and
            Mentioned ask you to act, and Team activity counts what is new since you looked.
            What you created asks nothing of you, so it keeps its link and has no count. */}
        <SideItem to="/inbox/created" icon={<UserIcon size={14} />} label="Created by me" collapsed={collapsed} />
        <SideItem to="/inbox/mentioned" icon={<AtSign size={14} />} label="Mentioned" collapsed={collapsed} count={inboxCounts?.mentioned} />
        <SideItem to="/inbox/team" icon={<Users size={14} />} label="Team activity" collapsed={collapsed} count={inboxCounts?.team} />
      </div>

      <div className={styles.section}>
        {!collapsed && (
          <div className={styles.label}>
            Projects
            <button
              type="button"
              className={styles.iconMini}
              aria-label="New project"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={12} />
            </button>
          </div>
        )}
        {visibleProjects.map((p) => (
          /**
           * The row is a wrapper rather than one link, because the settings
           * shortcut below is itself a link and an anchor cannot contain
           * another one. Nested, neither click would do what it looked like
           * it would.
           */
          <div key={p.id} className={styles.projRow}>
          {/* Collapsed, the row is only its colour dot: the name is the
              tooltip and the link's name, as for the other icons (TOADO-808). */}
          <CollapsedTooltip collapsed={collapsed} label={p.name}>
          <NavLink
            to={`/projects/${p.id}/board`}
            aria-label={collapsed ? p.name : undefined}
            className={({ isActive }: { isActive: boolean }) => cn(
              styles.item,
              isActive && styles.active,
              dragOverId === p.id && dragId !== p.id && styles.dropTarget,
              dragId === p.id && styles.dragging,
            )}
            // Drag-and-drop only when expanded — collapsed icons are too
            // narrow to hit accurately and there's no visual reorder
            // affordance in the collapsed state.
            draggable={!collapsed}
            onDragStart={!collapsed ? (e: DragEvent<HTMLAnchorElement>) => {
              setDragId(p.id);
              e.dataTransfer.setData('text/plain', p.id);
              e.dataTransfer.effectAllowed = 'move';
            } : undefined}
            onDragOver={!collapsed ? (e: DragEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverId !== p.id) setDragOverId(p.id);
            } : undefined}
            onDragLeave={!collapsed ? () => {
              if (dragOverId === p.id) setDragOverId(null);
            } : undefined}
            onDrop={!collapsed ? (e: DragEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              const sourceId = e.dataTransfer.getData('text/plain');
              if (sourceId) reorderTo(sourceId, p.id);
              setDragId(null);
              setDragOverId(null);
            } : undefined}
            onDragEnd={!collapsed ? () => {
              setDragId(null);
              setDragOverId(null);
            } : undefined}
          >
            <ProjectMark project={p} size={16} />
            {!collapsed && (
              <>
                {/* On the name, since the count gives way to the settings link on hover (TOADO-943). */}
                <Tooltip content={projectCountsLine(p)} delay={150}>
                  <span className={styles.itemLabel} data-testid="sidebar-project-name">{p.name}</span>
                </Tooltip>
                {p.hasUnreadActivity && <Tooltip content="New AI activity"><span className={styles.pulse} /></Tooltip>}
                {/* Open tickets only: what is left to do (TOADO-943). */}
                <span className={styles.count} data-testid="sidebar-project-count"><RollingCount value={openTickets(p)} /></span>
              </>
            )}
          </NavLink>
          </CollapsedTooltip>
          {/* Takes the place of the count on hover: the count is worth seeing
              at rest and worth nothing while somebody is reaching for this
              project's settings. Reachable by keyboard too, which is why the
              row reacts to focus-within and not only to hover. */}
          {!collapsed && (
            <Link
              to={`/projects/${p.id}/settings`}
              className={styles.projSettings}
              aria-label={`Settings for ${p.name}`}
              title={`Settings for ${p.name}`}
            >
              <Settings size={13} aria-hidden />
            </Link>
          )}
          </div>
        ))}
        <SideItem
          to="/projects"
          icon={<LayoutGrid size={14} />}
          label={projects.length > SIDEBAR_PROJECT_LIMIT ? `All projects (${projects.length})` : 'All projects'}
          collapsed={collapsed}
        />
      </div>

      {/* Renamed from the misleading "Admin" — these are general workspace
          links (members, settings), NOT the staff-only /admin panel, which
          now has its own gated section below. */}
      {/* Reports is about the whole workspace, so it leads here (Jason, 16 Oct). The bug report
          widget has its own link again (17 Oct): some people come looking for exactly that, and
          finding it only under Settings confused them. Owner-only, as its setup is. */}
      <CollapsibleNavSection title="Workspace" storageKey="workspace" railCollapsed={collapsed}>
        <SideItem to="/reports" icon={<BarChart3 size={14} />} label="Reports" collapsed={collapsed} section />
        <SideItem to="/company/settings/members" icon={<Users size={14} />} label="Members" collapsed={collapsed} />
        <SideItem to="/company/settings" icon={<Settings size={14} />} label="Settings" collapsed={collapsed} tourAnchor="sidebar.settings" />
        {isOwner && (
          <SideItem to="/company/settings/widgets" icon={<MessageSquare size={14} />} label="Bug report widget" collapsed={collapsed} />
        )}
        <SideItem to={EXTENSION_INSTALL_PATH} icon={<Download size={14} />} label="Get the extension" collapsed={collapsed} />
        <SideItem to="/me/settings/mcp-setup" icon={<Activity size={14} />} label="MCP setup" collapsed={collapsed} />
      </CollapsibleNavSection>

      {/* Below Workspace on purpose: these are steps somebody does once,
          and they were sitting above the links people use every day. The
          section still collapses itself once every step is done. */}
      <CollapsibleNavSection
        title="Get started"
        storageKey="get-started"
        defaultOpen={!gsAllDone}
        railCollapsed={collapsed}
      >
        <GetStartedNavItem
          to="/"
          icon={<Rocket size={14} />}
          label="Get started"
          done={gsHasProject}
          collapsed={collapsed}
        />
        <GetStartedExternalItem
          href={TOADO_CWS_URL}
          icon={<Chrome size={14} />}
          label="Install the extension"
          done={gsExtensionInstalled}
          collapsed={collapsed}
        />
        <GetStartedNavItem
          to={widgetSetupTarget}
          icon={<MessageSquare size={14} />}
          label="Set up the widget"
          done={gsWidgetInstalled}
          collapsed={collapsed}
        />
        <GetStartedNavItem
          to="/me/settings/mcp-setup"
          icon={<Plug size={14} />}
          label="Connect MCP"
          done={gsMcpConfigured}
          collapsed={collapsed}
        />
        <GetStartedActionItem
          icon={<Sparkles size={14} />}
          label="Get the skill"
          done={gsSkillDownloaded}
          collapsed={collapsed}
          onClick={() => {
            downloadSkill();
            setSkillDownloaded(true);
          }}
          ariaLabel="Download the Claude Code skill"
        />
      </CollapsibleNavSection>

      {/* Staff-only admin panel, surfaced here so its nav lives in the main
          sidebar (the interior /admin aside is gone). Gated the same way as
          the TopBar Admin entry; the server 404s /admin/* for non-staff
          regardless, so this is UX only. */}
      {user?.isStaff === true && (
        <CollapsibleNavSection
          title="Admin"
          storageKey="admin"
          defaultOpen={location.pathname.startsWith('/admin')}
          railCollapsed={collapsed}
        >
          <SideItem to="/admin/users" icon={<Users size={14} />} label="Users" collapsed={collapsed} />
          <SideItem to="/admin/accounts" icon={<Building2 size={14} />} label="Accounts" collapsed={collapsed} />
          <SideItem to="/admin/usage" icon={<Gauge size={14} />} label="Usage" collapsed={collapsed} />
          <SideItem to="/admin/cancellations" icon={<XCircle size={14} />} label="Cancellations" collapsed={collapsed} />
          <SideItem to="/admin/analytics" icon={<BarChart3 size={14} />} label="Analytics" collapsed={collapsed} />
          <SideItem to="/admin/overrides" icon={<SlidersHorizontal size={14} />} label="Overrides" collapsed={collapsed} />
          <SideItem to="/admin/plan-limits" icon={<Gauge size={14} />} label="Plan limits" collapsed={collapsed} />
          <SideItem to="/admin/provisioning" icon={<Briefcase size={14} />} label="Provisioning" collapsed={collapsed} />
          <SideItem to="/admin/content" icon={<MonitorPlay size={14} />} label="Content" collapsed={collapsed} />
        </CollapsibleNavSection>
      )}

      <CreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        existingColors={projects.map((p) => p.color)}
      />
    </aside>
  );
}

/** Grayscale completion check rendered to the right of a Get-started
 *  item label. Hidden when the sidebar is collapsed (no room) and
 *  hidden when the step isn't done (no visual noise on incomplete
 *  rows). Uses --text-tertiary so the check reads as "noted, not the
 *  active action" — green or accent would draw the eye too much. */
function CompletionCheck({ done, collapsed }: { done: boolean; collapsed: boolean }) {
  if (collapsed || !done) return null;
  return <Check size={12} className={styles.gsCheck} aria-hidden="true" />;
}

/** Internal-route Get started item. Same visual rhythm as SideItem
 *  but with a completion check appended. */
function GetStartedNavItem({
  to, icon, label, done, collapsed,
}: { to: string; icon: ReactNode; label: string; done: boolean; collapsed: boolean }) {
  const node = (
    <NavLink to={to} end className={({ isActive }: { isActive: boolean }) => cn(styles.item, isActive && styles.active)}>
      <span className={styles.itemIcon}>{icon}</span>
      {!collapsed && <span className={styles.itemLabel}>{label}</span>}
      <CompletionCheck done={done} collapsed={collapsed} />
    </NavLink>
  );
  return collapsed ? <Tooltip content={label} placement="right">{node}</Tooltip> : node;
}

/** External-link item (e.g. Chrome Web Store). Renders as a styled
 *  <a target="_blank">, not a NavLink — the destination isn't an in-
 *  app route. */
function GetStartedExternalItem({
  href, icon, label, done, collapsed,
}: { href: string; icon: ReactNode; label: string; done: boolean; collapsed: boolean }) {
  const node = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.item}
    >
      <span className={styles.itemIcon}>{icon}</span>
      {!collapsed && <span className={styles.itemLabel}>{label}</span>}
      <CompletionCheck done={done} collapsed={collapsed} />
    </a>
  );
  return collapsed ? <Tooltip content={label} placement="right">{node}</Tooltip> : node;
}

/** Click-action item (e.g. trigger a download). Renders as a button
 *  styled identically to NavLink for visual consistency in the rail. */
function GetStartedActionItem({
  icon, label, done, collapsed, onClick, ariaLabel,
}: {
  icon: ReactNode;
  label: string;
  done: boolean;
  collapsed: boolean;
  onClick: () => void;
  ariaLabel: string;
}) {
  const node = (
    <button
      type="button"
      className={cn(styles.item, styles.itemBtn)}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <span className={styles.itemIcon}>{icon}</span>
      {!collapsed && <span className={styles.itemLabel}>{label}</span>}
      <CompletionCheck done={done} collapsed={collapsed} />
    </button>
  );
  return collapsed ? <Tooltip content={label} placement="right">{node}</Tooltip> : node;
}

/** A count beside a list's name: none at zero, and "99+" above 99 (TOADO-740). */
function shownCount(count: number | undefined): string | null {
  if (!count || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

function SideItem({ to, icon, label, collapsed, count, section = false, tourAnchor }: {
  to: string; icon: React.ReactNode; label: string; collapsed: boolean; count?: number;
  /** The `data-tour-anchor` a tour points at this item by (TOADO-988). */
  tourAnchor?: string;
  /** Selected on the pages under `to` too: Reports, whose pages are its own subnavigation. */
  section?: boolean;
}) {
  // `end` makes NavLink active only on exact path equality. Without it,
  // React Router lights up any item whose `to` is an ancestor of the
  // current URL — so on /projects/p-x/board, both "All projects"
  // (to=/projects) AND the project pin would render as active. Same
  // problem for /company/settings vs /company/settings/members. SideItems
  // are leaf-style nav targets, so end:true is the default; `section` is
  // for the one whose pages have no sidebar items of their own.
  const node = (
    <NavLink to={to} end={!section} data-tour-anchor={tourAnchor} className={({ isActive }: { isActive: boolean }) => cn(styles.item, isActive && styles.active)}>
      <span className={styles.itemIcon}>{icon}</span>
      {!collapsed && <span className={styles.itemLabel}>{label}</span>}
      {!collapsed && shownCount(count) && <span className={styles.count} data-testid="sidebar-count"><RollingCount value={shownCount(count)!} /></span>}
    </NavLink>
  );
  return <CollapsedTooltip collapsed={collapsed} label={label}>{node}</CollapsedTooltip>;
}

/** The name of an icon-only row, on hover and focus, while the sidebar is collapsed. */
function CollapsedTooltip({ collapsed, label, children }: { collapsed: boolean; label: string; children: React.ReactElement }) {
  return collapsed ? <Tooltip content={label} placement="right">{children}</Tooltip> : children;
}
